import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  Upload,
  FileText,
  Send,
  Mail,
  Copy,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import api, { adminReq } from "../../lib/api";
import { toast } from "sonner";

const DEFAULT_SUBJECT = "Your Cambridge College Recruiter Agreement";
const DEFAULT_BODY = (r) =>
  `Dear ${r.full_name},

Thank you for applying to become a recruitment partner with Cambridge College of Technology Inc.

Please review and electronically sign your recruiter agreement using the secure link below. By signing, you confirm acceptance of the Cambridge College partner terms, commission schedule, and code of conduct.

If you have any questions prior to signing, reply to this email and our partnerships team will be happy to help.

We look forward to welcoming ${r.company_name} to the Cambridge partner network.

Kind regards,
Cambridge College Admissions
info@cambridgecollegebc.com`;

export default function SendAgreementModal({ recruiter, onClose, onDone }) {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY(recruiter));
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);

  const send = async () => {
    if (!subject.trim() || !body.trim())
      return toast.error("Subject and body are required");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject);
      fd.append("body", body);
      if (file) fd.append("agreement_file", file);
      const { data } = await api.post(
        `/admin/recruiters/${recruiter.id}/send-agreement`,
        fd,
        adminReq()
      );
      const fullUrl = `${window.location.origin}${data.signing_url}`;
      setSent({ ...data, fullUrl });
      toast.success("Agreement sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send agreement");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ type: "spring", damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl my-6 bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header bar styled like an email compose */}
        <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2A4FAE] px-5 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-300" />
            <div className="text-sm font-semibold">
              {sent ? "Agreement sent" : "Send Recruiter Agreement"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">
                  Agreement delivered
                </div>
                <div className="text-xs text-slate-500">
                  Share the signing link with {recruiter.email} to collect
                  their e-signature.
                </div>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Signing link
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[12px] text-slate-700 bg-white border border-slate-200 rounded-md px-2 py-1.5 truncate">
                  {sent.fullUrl}
                </code>
                <button
                  onClick={() => copy(sent.fullUrl)}
                  className="inline-flex items-center gap-1 bg-cct-blue text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-[#1a3380]"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            </div>
            <div className="text-[12px] text-slate-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
              <span>
                In production, this link is automatically emailed to the
                recruiter. For now, copy and send it manually.
              </span>
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={() => {
                  onDone();
                }}
                className="bg-cct-blue text-white text-sm font-semibold px-5 py-2 rounded-md hover:bg-[#1a3380]"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <EmailRow label="To">
              <span className="text-sm text-slate-800 font-medium">
                {recruiter.full_name}
              </span>
              <span className="text-xs text-slate-500 ml-2">
                &lt;{recruiter.email}&gt;
              </span>
            </EmailRow>
            <EmailRow label="Subject">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-transparent text-sm text-slate-900 outline-none"
              />
            </EmailRow>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Message
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-sm text-slate-800 input-focus font-mono"
                style={{ fontFamily: "inherit" }}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 block">
                Attach Agreement (PDF or image)
              </label>
              {file ? (
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <FileText className="w-4 h-4 text-cct-blue" />
                  <span className="text-sm text-slate-700 truncate flex-1">
                    {file.name}
                  </span>
                  <button
                    onClick={() => setFile(null)}
                    className="text-xs text-slate-500 hover:text-red-500"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-dashed border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-600 cursor-pointer w-full">
                  <Upload className="w-4 h-4 text-cct-blue" />
                  <span className="flex-1">Click to attach agreement file</span>
                  <span className="text-[11px] text-slate-400">
                    PDF, JPG, PNG · up to 10MB
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
              )}
            </div>
            <div className="text-[11px] bg-sky-50 border border-sky-100 rounded-lg px-3 py-2 text-sky-800">
              An electronic signing link will be appended to this message.
              The recruiter signs by typing their legal name as an e-signature.
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <button
                onClick={onClose}
                className="text-sm text-slate-500 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={send}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-cct-blue text-white text-sm font-semibold px-5 py-2 rounded-md hover:bg-[#1a3380] disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Agreement"}
                {!submitting && <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function EmailRow({ label, children }) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-100 pb-2">
      <div className="w-16 text-[11px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">
        {label}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
